/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { ContentBlock, Heading, Text, Button, Icon, CardCarousel } from 'sarsaparilla';

import { useUserLocation } from 'shared-ui/geolocation';

import { useNearWaterQuery, RECOMMENDATION_RADIUS } from './api/search-api.queries';
import { getNextWeekend } from './utils/getNextWeekend';

import { CarouselCard } from './CarouselCard/CarouselCard';

import { RecommendationCarouselSkeleton } from './RecommendationCarouselSkeleton';
import { dateToSillySearchFormat } from './utils';

/**
 * Renders a carousel of campsites near water.
 */
export function NearWater() {
    const userLocation = useUserLocation();

    const { data, isPending, isError } = useNearWaterQuery({
        isEnabled: userLocation !== null,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
    });

    const cards = React.useMemo(() => {
        if (!data?.results) {
            return [];
        }

        return data?.results?.map((item) => {
            return (
                <CarouselCard
                    key={item.id}
                    item={item}
                    showAvailDates
                    onClick={() => {}}
                    gaTrackingId="294567527392"
                    gaTrackingValue={item.name}
                />
            );
        });
    }, [data?.results]);

    const exploreMoreHref = React.useMemo(() => {
        if (!userLocation) {
            return '';
        }

        return getExploreMoreHref(userLocation.latitude, userLocation.longitude);
    }, [userLocation]);

    if (isPending) {
        return <RecommendationCarouselSkeleton />;
    }

    // Render nothing is there's nothing to show
    if (isError || !data?.results || data.results.length <= 4) {
        return null;
    }

    return (
        <section>
            <ContentBlock>
                <Heading headingLevel={2} appearance="h4" hasUnderline="left">
                    Near Water
                </Heading>
                <Text className="mb-2">
                    Campgrounds with upcoming availability and nearby water amenities.{' '}
                    <Button
                        appearance="link"
                        href={exploreMoreHref}
                        iconAfterElement={<Icon iconName="arrow-forward" />}
                        className="ml-half"
                        style={{ verticalAlign: 'baseline' }}
                        aria-label="Explore more campsites near water"
                        gaTrackingId="728316586994"
                    >
                        Explore More
                    </Button>
                </Text>
            </ContentBlock>

            <CardCarousel
                accessibleName="Near water"
                gaTrackingId="957550862660"
                overscanPerBreakpoint={{ md: 8 }}
                simulateContentBlock
                peekPerBreakpoint={{ xs: 24, md: 0 }}
            >
                {cards}
            </CardCarousel>
        </section>
    );
}

function getExploreMoreHref(latitude: number, longitude: number): string {
    const [startDate, endDate] = getNextWeekend();

    const searchParams = new URLSearchParams({
        inventory_type: 'camping',
        checkin: dateToSillySearchFormat(startDate),
        checkout: dateToSillySearchFormat(endDate),
        sort: 'score',
        lat: latitude.toString(),
        lng: longitude.toString(),
        clear: 'true',
        special_site: 'boat',
        access: 'Boat-in',
        radius: RECOMMENDATION_RADIUS,
    });

    const activityParams = [
        'swimming',
        'boating',
        'diving',
        'fishing',
        'water sports',
        'snorkeling',
    ];

    activityParams.forEach((activity) => {
        searchParams.append('activity', activity);
    });

    return `/search?${searchParams.toString()}`;
}

// cSpell:ignore checkin
